// =====================================================
// session.js one place that answers "who is signed in?"
//
// INTEGRATION POINT (the only one):
// Wherever your existing login finishes (the "Hi Mike,
// welcome back" moment), add ONE line:
//
//    setSession({ email, firstName });
//
// and call clearSession() on sign-out. Everything else
// (favorites, profile hub, signup-intent) reads from here.
// =====================================================

import { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

// If you already store the signed-in user under a different
// localStorage key, either change this key or keep both in
// sync this is the key the new features read.
const STORAGE_KEY = "gohubSession";

// Where a guest is sent when they tap "Sign in / create account".
// PROVISIONAL: set to your SignInModal route based on App.js.
// If your user sign-in ("Hi Mike") lives elsewhere, change this
// one line. Do NOT point it at /partner-signup that is the
// vendor/business flow, not user sign-in.
export const SIGN_IN_ROUTE = "/SignInModal";

// Business detail page route. Currently UNUSED the app has no
// /business/:id page yet, so ProfileHub shows saved places as
// plain entries. When a detail page exists, set the path here.
export const businessRoute = (businessId) =>
  `/business/${encodeURIComponent(businessId)}`;

export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.email) return null;

    // Shape: { email, firstName?, token? }
    return parsed;
  } catch {
    return null;
  }
}

export function setSession(session) {
  if (!session?.email) return;

  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      email: String(session.email).trim().toLowerCase(),
      firstName: session.firstName || "",
      token: session.token || "",
    })
  );

  window.dispatchEvent(new Event("gohub-session"));
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("gohub-session"));
}

/*
 * React hook re-renders the component whenever the session
 * changes (same tab via our custom event, other tabs via the
 * browser's storage event).
 */
export function useSession() {
  const [user, setUser] = useState(getCurrentUser);

  useEffect(() => {
    const sync = () => setUser(getCurrentUser());

    window.addEventListener("gohub-session", sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener("gohub-session", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return user;
}

/*
 * Pre-authed axios instance. Every request automatically
 * carries who the user is.
 *
 * SECURITY NOTE: the x-user-email header is fine for building
 * and testing, but it can be spoofed. Before launch, have your
 * login return a session token, store it in setSession, and
 * make the backend verify the Authorization header instead of
 * trusting the email header. Say the word and I'll wire that.
 */
export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const user = getCurrentUser();

  if (user?.email) {
    config.headers["x-user-email"] = user.email;
  }
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }

  return config;
});
